import {SlackNotificationSettingsForm} from "../../api/Model";
import {inputChange, mapSetState, SetState} from "../../util/FormHandlers";
import React, {useMemo} from "react";
import {FormControlLabel, IconButton, InputAdornment, Stack, Switch, TextField, Tooltip} from "@mui/material";
import {CronField} from "../../components/cron-field/CronField";
import {ProblemTypeSelect} from "../../components/problem-type-select/ProblemTypeSelect";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export function SlackSettingsFormComponent({form, setForm}: {
    form: SlackNotificationSettingsForm;
    setForm: SetState<SlackNotificationSettingsForm>;
}) {
    const handlers = useMemo(() => ({
        handleNameChange: inputChange(setForm, newValue => ({
            name: newValue,
        })),
        handleChannelIdChange: inputChange(setForm, newValue => ({
            channelId: newValue,
        })),
        setProblemTypes: mapSetState(setForm, 'problemTypes'),
        setCron: mapSetState(setForm, 'cron'),
        handleEnabledChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            setForm(prev => ({...prev, enabled: event.target.checked}));
        }
    }), [setForm]);

    return (
        <Stack spacing={2} direction="column" sx={{pt: 2}}>
            <TextField
                required
                margin="dense"
                id="name"
                label="Name"
                placeholder="E.g., Berlin Weekly Report"
                type="text"
                fullWidth
                variant="outlined"
                value={form.name}
                onChange={handlers.handleNameChange}
            />
            <TextField
                required
                margin="dense"
                id="channelId"
                label="Slack Channel ID / User ID"
                placeholder="C0123456789 or U06NPDWN28G"
                helperText="The channel or user ID of the Slack channel or user to send notifications to."
                type="text"
                fullWidth
                variant="outlined"
                value={form.channelId}
                onChange={handlers.handleChannelIdChange}
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip
                                    title={
                                        <div style={{whiteSpace: 'pre-line'}}>
                                            <strong>User ID:</strong> Found in user profile (More &gt; Copy member ID). Typically starts with 'U' (e.g., U06NPDWN28G).{'\n\n'}
                                            <strong>Channel ID:</strong> Found in channel details (Click channel name &gt; bottom of 'About' tab). Typically starts with 'C' (e.g., C0123456789).{'\n\n'}
                                            <strong>Important:</strong> For channels, you must add the Slack application to the channel first.
                                        </div>
                                    }
                                    arrow
                                >
                                    <IconButton edge="end" size="small">
                                        <HelpOutlineIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <ProblemTypeSelect
                margin="dense"
                id="problem-types"
                label="Problem Filter"
                fullWidth
                variant="outlined"
                value={form.problemTypes}
                setValue={handlers.setProblemTypes}
                helperText="Report all problems if none selected"
            />
            <CronField
                required
                margin="dense"
                id="cron"
                label="CRON Period"
                type="text"
                fullWidth
                variant="outlined"
                value={form.cron}
                setValue={handlers.setCron}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={form.enabled}
                        onChange={handlers.handleEnabledChange}
                    />
                }
                label="Enabled"
            />
        </Stack>
    );
}
